import { FC, ReactNode, useCallback, useState } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'
import {
  Text,
  GreyGrey60,
  Primary13SemiBold,
  Flex
} from '@sequencehq/core-components'
import {
  activeVersionIdAtom,
  editingVersionAtom,
  lastSavedPresentationAtom,
  presentationAtom,
  resetPresentationHistoryAtom,
  versionPreviewAtom
} from 'Automations/automationEditor/atoms'
import { ModalRestoreVersion } from 'Automations/automationEditor/components/restoreVersion.modal'
import { SidebarTemplate } from '../../components'
import { VersionHistoryItem } from './VersionHistoryItem'

export const CurrentVersion: FC = () => {
  const editingVersion = useAtomValue(editingVersionAtom)
  const activeVersionId = useAtomValue(activeVersionIdAtom)
  const [showModal, setShowModal] = useState(false)
  const resetToCurrentVersion = useResetToCurrentVersion()

  if (!editingVersion) return null

  return (
    <Wrapper>
      <VersionHistoryItem
        automationVersion={{
          ...editingVersion,
          active: editingVersion.id === activeVersionId
        }}
        isStatic
        onClick={() => setShowModal(true)}
      />
      {showModal && (
        <ModalRestoreVersion
          onClose={() => setShowModal(false)}
          onRestore={resetToCurrentVersion}
        />
      )}
    </Wrapper>
  )
}

const useResetToCurrentVersion = () => {
  const lastSavedPresentation = useAtomValue(lastSavedPresentationAtom)
  const setPresentation = useSetAtom(presentationAtom)
  const setVersionPreview = useSetAtom(versionPreviewAtom)
  const resetHistory = useSetAtom(resetPresentationHistoryAtom)

  return useCallback(() => {
    if (!lastSavedPresentation) {
      throw new Error('lastSavedPresentation is undefined')
    }

    setPresentation(lastSavedPresentation)
    setVersionPreview(undefined)
    resetHistory()
  }, [lastSavedPresentation, setPresentation, setVersionPreview, resetHistory])
}

const Wrapper: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Flex
      data-testid="automations.editor.versionHistory.currentVersion"
      flexDir="column"
      gap="6px"
    >
      <SidebarTemplate.Section>
        <Text
          data-testid="automations.editor.versionHistory.currentVersion.label"
          {...Primary13SemiBold}
          color={GreyGrey60}
        >
          Current
        </Text>
      </SidebarTemplate.Section>

      {children}
    </Flex>
  )
}
